import { useMemo, useState } from 'react';
import { Chess } from 'chess.js';
import Chessboard from '../board/Chessboard';
import type { MoveMistake } from '../../../shared/types';

/**
 * A tempting wrong move, played out on the board so you can watch it fail.
 *
 * Being told "don't play ...Na5 here" gives you a rule to memorise. Watching
 * the knight get trapped four moves later gives you a reason you can rebuild at
 * the board when the position is not quite the same — which is the difference
 * between knowing an opening and having memorised one.
 *
 * The refutation is stepped through rather than dumped as a move list, because
 * the point is watching *what happens*, and the consequence is withheld until
 * the end so the position makes its own case first.
 */
export default function MistakeExplorer({
  /** Moves of the main line up to (not including) the correct move. */
  prefix,
  mistake,
  orientation,
}: {
  prefix: string[];
  mistake: MoveMistake;
  orientation: 'white' | 'black';
}) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  // Every position from the mistake through the end of the refutation.
  //
  // This used to assume replay could not fail, because the build validates
  // every move on a real board. It validates each *line* and each *refutation*
  // in isolation, though -- never the pairing of a prefix with a mistake, which
  // is composed here at render time from two different pieces of state. When a
  // study session moved to the next line, one frame rendered the previous
  // line's mistake against the new line's position, chess.js threw, and the
  // whole page went to the error boundary.
  //
  // The pairing is fixed at the source now, but a component that takes two
  // independently-valid inputs and combines them should not be able to bring
  // the page down when they disagree. Returning nothing is right for the
  // transient case and honest for the permanent one: there is no mistake here
  // that can be shown.
  const positions = useMemo(() => {
    const board = new Chess();
    const out: { fen: string; san: string; note?: string; from: string; to: string }[] = [];
    try {
      prefix.forEach((san) => board.move(san));
      const play = (san: string, note?: string) => {
        const m = board.move(san);
        out.push({ fen: board.fen(), san, note, from: m.from, to: m.to });
      };
      play(mistake.san);
      mistake.refutation.forEach((r) => play(r.san, r.note));
    } catch {
      return [];
    }
    return out;
  }, [prefix, mistake]);

  if (positions.length === 0) return null;

  const at = positions[Math.min(step, positions.length - 1)];
  const atEnd = step >= positions.length - 1;

  if (!open) {
    return (
      <button type="button" className="mistake-open" onClick={() => setOpen(true)}>
        <span className="mistake-label">Common mistake</span>
        <strong>{mistake.san}</strong>
        <span className="mistake-cta">see what happens →</span>
      </button>
    );
  }

  return (
    <div className="mistake">
      <div className="mistake-head">
        <h4>
          If you play <strong>{mistake.san}</strong>
        </h4>
        <button type="button" className="btn btn--small" onClick={() => setOpen(false)}>
          Close
        </button>
      </div>
      <p className="mistake-why">{mistake.why}</p>

      <Chessboard fen={at.fen} orientation={orientation} disabled lastMove={{ from: at.from, to: at.to }} maxSize={300} />

      <div className="mistake-controls">
        <button
          type="button"
          className="btn btn--small"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
        >
          ←
        </button>
        <span className="mistake-move">
          {at.san}
          {at.note && <span className="mistake-note"> — {at.note}</span>}
        </span>
        <button
          type="button"
          className="btn btn--small btn--primary"
          onClick={() => setStep((s) => Math.min(positions.length - 1, s + 1))}
          disabled={atEnd}
        >
          →
        </button>
      </div>

      {/* Held back until the end: the position should make the case first, and
          reading the verdict before seeing it happen is how you end up
          memorising the verdict instead of understanding the position. */}
      {atEnd ? (
        <p className="mistake-consequence">{mistake.consequence}</p>
      ) : (
        <p className="muted mistake-hint">
          Step forward to see how it is punished ({positions.length - 1 - step} to go).
        </p>
      )}
    </div>
  );
}
